import React, { useCallback, useEffect, useRef, useState } from 'react'
import useDebounce from 'react-use/lib/useDebounce' // TODO: revert import change when this issue is resolved: https://github.com/streamich/react-use/issues/2074
import { AddressFields } from 'modules'
import { Button, JSONFetcher } from 'shared'
import { Form, Formik, connect } from 'formik'
import { FormikSubmitHandler, LocationEditorFormValues, LocationEditorProp, MapOuterProps } from './types'

export const LocationEditor = ({
  url,
  zoom,
  city,
  street,
  zip,
  country,
  lat,
  lng,
}: LocationEditorProp): JSX.Element => {
  /** @type {LocationEditorFormValues} */
  const initialValues: LocationEditorFormValues = {
    city: city || '',
    street: street || '',
    zip: zip || '',
    country_alpha2: country || 'de',
    lat: lat ? parseFloat(lat) : 0,
    lng: lng ? parseFloat(lng) : 0,
  }
  /** @type {FormikSubmitHandler} */
  const submitHandler: FormikSubmitHandler = useCallback(
    (values, formikBag) => {
      JSONFetcher.put({
        url,
        body: { location: values },
        success: (_response) => {
          formikBag.setSubmitting(false)
          window.location.reload()
        },
      })
    },
    [url]
  )

  return (
    <Formik initialValues={initialValues} onSubmit={submitHandler}>
      {(formikProps) => (
        <Form>
          <div className="max-w-md mb-4">
            <AddressFields />
            <Map zoom={zoom} />
            <div className="mt-2 text-sm text-muted">{i18n.t('manage.location_editor.map_hint')}</div>
          </div>

          <Button type="submit" className="btn btn-primary btn-large" loading={formikProps.isSubmitting}>
            {i18n.t('manage.location_editor.submit')}
          </Button>
        </Form>
      )}
    </Formik>
  )
}

const Map = connect<MapOuterProps, LocationEditorFormValues>(({ formik, zoom }) => {
  const mapContainer = useRef<HTMLDivElement | null>(null)
  const map = useRef<google.maps.Map | null>(null)
  const marker = useRef<google.maps.Marker | null>(null)

  const [loading, setLoading] = useState(true)

  const geocode = useCallback(async () => {
    if (loading) return setLoading(false)

    const geocoder = new google.maps.Geocoder()

    await geocoder.geocode(
      { address: `${formik.values.street}, ${formik.values.zip} ${formik.values.city}` },
      (results) => {
        const position = results && results[0]?.geometry?.location
        if (position) {
          map.current?.panTo(position)
          map.current?.setZoom(15)
          marker.current?.setPosition(position)
          void formik.setFieldValue('lat', position.lat())
          void formik.setFieldValue('lng', position.lng())
        }
      }
    )
  }, [formik, loading])

  useDebounce(geocode, 500, [formik.values.street, formik.values.zip, formik.values.city])

  useEffect(() => {
    if (!mapContainer.current) return
    const position = { lat: formik.values.lat, lng: formik.values.lng }
    map.current = new google.maps.Map(mapContainer.current, {
      zoom: parseInt(zoom),
      center: position,
      streetViewControl: false,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
      mapTypeControlOptions: { mapTypeIds: [] },
      fullscreenControl: false,
    })

    marker.current = new google.maps.Marker({
      map: map.current,
      position: position,
      draggable: true,
    })
    google.maps.event.addListener(marker.current, 'dragend', () => {
      const position = marker.current?.getPosition()
      if (!position) return
      void formik.setFieldValue('lat', position.lat())
      void formik.setFieldValue('lng', position.lng())
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <div ref={mapContainer} style={{ height: '300px' }}></div>
})
